import { GlobalSettingStore } from './../stores/global-setting.store';
import { DashboardHelper } from './../dashboard.service';
import { WorkspaceStore } from './../stores/workspace.store';
import { ExternalLinkStore } from '../stores/external-link.store';
import { AppSettingStore } from '../stores/app-setting.store';
import { Injectable } from '@angular/core';
import { DirectoryStore } from '../stores/directory.store';
import { GroupStore } from '../stores/group.store';
import { LicenseStore } from '../stores/license.store';
import { UserStore } from '../stores/user.store';
import { ApplicationStore } from '../stores/application.store';
import { UserService } from '../user.service';
import { OrganizationStore } from '../stores/organization.store';
import { PermissionService } from '../permission.service';
import { ProjectStore } from '../stores/projects.store';
import { EmailManagerStore } from '../stores/email-manager.store';
import { PermissionStore } from '../stores/permission.store';
import { RoleStore } from '../stores/role.store';
import { AttachmentsStore } from '../stores/attachments.store';
import { BlueprintStore } from '../stores/blueprint.store';
import { UiStateStore } from '../stores/ui-state.store';

@Injectable({
  providedIn: 'root',
})
export class AppInitHelper {
  private isInitialDataLoaded = false;
  constructor(
    private appSettingStore: AppSettingStore,
    private externalLinkStore: ExternalLinkStore,
    private directoryStore: DirectoryStore,
    private userStore: UserStore,
    private groupStore: GroupStore,
    private licenseStore: LicenseStore,
    private userService: UserService,
    private organizationStore: OrganizationStore,
    private attachmentStore: AttachmentsStore,
    private permissionService: PermissionService,
    private applicationStore: ApplicationStore,
    private projectStore: ProjectStore,
    private workspaceStore: WorkspaceStore,
    private bluePrintStore: BlueprintStore,
    private emailManagerStore: EmailManagerStore,
    private permissionStore: PermissionStore,
    private roleStore: RoleStore,
    private dashboardHelper: DashboardHelper,
    private globalSettingStore: GlobalSettingStore,
    private uiStateStore: UiStateStore,
  ) {
    // Reset load flag on any logout so the next login reloads all stores.
    // Manual logout goes via unloadData() which also resets it; this covers
    // the session-timeout path that calls userService.logout() directly.
    this.userService.userIsLoggedOut.subscribe((isLoggedOut) => {
      if (isLoggedOut) {
        this.isInitialDataLoaded = false;
      }
    });
    // this.dashboardHelper.$currentApplication.subscribe((x) => {
    //   if (x) {
    //     if (this.applicationStore.catchInternalApplicationNameList.includes(x.productType.toLocaleLowerCase())) {
    //       this.loadChildApp();
    //     }
    //   }
    // });
  }

  public initApplication(): void {
    this.appSettingStore.loadInitialData();
  }

  public loadInitialData(): void {
    if (this.isInitialDataLoaded) {
      return;
    }
    this.isInitialDataLoaded = true;
    if (!this.permissionService.isSysAdmin) {
      this.organizationStore.clearOrgData();
    }
    // Logging out navigates the router to the login page, it does not reload the page, so these
    // root-provided stores keep the outgoing user's data and - being BehaviorSubjects - replay it
    // to whoever subscribes next. Left alone, the incoming dashboard re-selects the previous
    // session's workspace and asks the API for its blueprint; when the two logins are different
    // organisations the API refuses that cross-org read with a 404, surfacing as the generic
    // "Invalid URL...! Please contact support." toast.
    //
    // Cleared here rather than in unloadData() on purpose. At this point the caller is still on
    // the login screen and the previous dashboard is gone, so nothing is listening. Clearing at
    // logout instead emits into components that are still mounted: the comment panel treats the
    // reset workspace as a real selection and refetches with an undefined workspace id.
    this.workspaceStore.clearTenantState();
    this.bluePrintStore.clearTenantState();
    this.initStores();
  }

  public unloadData(): void {
    this.isInitialDataLoaded = false;
    // this.resetStores();
    // this.resetHelpers();
    // this.workspaceStore.unloadData();
    //Todo: this is a temporray fix for added workspace instance not being handled properly when user logging out and logging in again, need to be addressed in 7.15/8.0.0
    this.workspaceStore.resetAddeworkspace();
    // close socket commented out for ZOO-6011 console error issue. Need to be addressed in 7.15/8.0.0
    // this.socketService.closeSocket();
    this.dashboardHelper.setShowRightPanel(false);
    this.dashboardHelper.showVideoList = false;
    this.projectStore.resetProjects();
    this.userService.logout(); // TODO - need to remove this
  }

  // public loadChildApp(): void {
  //   //this.workspaceStore.sortWorkspaceViewModelList('key', SortDirection.ASC);
  // }

  private initStores(): void {
    // login.component also calls this, but a page refresh goes straight to the dashboard without
    // touching login - without it here startBackendAction() would never show its progress dialog
    this.uiStateStore.loadInitialData();
    this.organizationStore.loadInitialData();
    this.bluePrintStore.loadInitialData();
    this.attachmentStore.loadInitialData();
    this.externalLinkStore.loadOrgExternalLinks(this.userService.user?.organizationId);
    this.applicationStore.loadInitialData();
    this.directoryStore.loadInitialData();
    this.licenseStore.loadInitialData();
    this.userStore.loadInitialData(this.userService.user?.organizationId);
    this.groupStore.loadInitialData();
    this.emailManagerStore.loadInitialData();
    this.permissionStore.loadInitialData();
    // Not roleStore.loadInitialData() here (ZOO-9924) - GET /api/roles is role:read-gated
    // (admin-only, RbacDefaults), and every roleStore consumer outside this file lives under the
    // admin Roles/Groups/Users screens, each of which already calls loadInitialData() itself in its
    // own ngOnInit (e.g. role-list.component.ts:78). Loading it unconditionally for every session
    // only 403'd every non-admin user on login without any screen actually needing the preload.
    this.projectStore.loadInitialData();
    this.globalSettingStore.loadPublicData();

    //currently for workspacestore, we don't have a method for loading initial data like other stores, so once workspace store destroyed, there will be issues.
  }

  //TODO - 7.15 we need to implement proper way for reset data stores when logout
  // need to undestand after logout and login how to restart services
  // services restart normally happening in bootup

  // private resetStores(): void {
  //   this.appSettingStore.unloadData();
  //   this.applicationStore.unloadData();
  //   this.bluePrintStore.unloadData();
  //   this.classicETStore.unloadData();
  //   this.commentStore.unloadData();
  //   this.directoryStore.unloadData();
  //   this.emailManagerStore.unloadData();
  //   this.externalLinkStore.unloadData();
  //   this.groupStore.unloadData();
  //   this.jiraStore.unloadData();
  //   this.workspaceStore.unloadData();
  //   this.userStore.unloadData();
  //   this.permissionStore.unloadData();
  //   this.projectStore.unloadData();
  //   //this.uiStateStore.unloadData();
  //   //this.licenseStore.unloadData();

  //   //Pls don't uncomment following detailStore and organizationStore unloadData() method
  //   //this.detailStore.unloadData();
  //   //this.organizationStore.unloadData();
  // }

  // private resetHelpers(): void {
  //   this.blueprintDataHelper.unloadData();
  // }
}
